import { create } from 'zustand'
import crashSocket from '../services/crashSocket'
import { crashApi } from '../services/crashApi'
import { hapticNotify, hapticImpact } from '../../../utils/haptic'
import {
  PHASES,
  CLIENT_EVENTS,
  WIN_SKIN_TIMEOUT_MS,
  CASHOUT_CONFIRM_TIMEOUT_MS
} from '../config/crashConfig'

/**
 * Нормализация записи игрока из crash:bet / crash:bets.
 * В событии ставки нет полей вывода — явно сбрасываем их, чтобы при мерже
 * не «протекли» cashed/win из прошлого раунда.
 */
const normalizeBet = (bet) => ({
  ...bet,
  cashed: false,
  cashoutMultiplier: null,
  win: 0,
  winSkin: null
})

/**
 * Store режима Краш
 *
 * ВАЖНО (§4, §10 краш-документации):
 * - players хранятся в Map по chatId, обновляются идемпотентно (перезапись, не push):
 *   свои события приходят дважды (личное + пачка), дубль не должен ломать состояние.
 * - balance и inventory НЕ считать локально — только из crash:balance / crash:inventory.
 * - roundId — строка 32 символа, сравнивать через === без приведения.
 * - множитель хранится в сотых (214 = 2.14×). Делить на 100 только при выводе на экран.
 */
const useCrashStore = create((set, get) => ({
  // ──────────────────────────── Состояние подключения ────────────────────────────
  isConnected: false,           // socket.connected
  hasSnapshot: false,
  hasRoundSnapshot: false,
  isAuthorized: false,          // признак: пришёл crash:balance (наблюдателю не приходит)
  showReconnectNotice: false,  // показывать индикатор «переподключаемся» (через ~3с)

  // ──────────────────────────── Текущий раунд ────────────────────────────
  phase: PHASES.IDLE,
  // Режим выключен админом (crash:disabled). Отдельный флаг, а не фаза:
  // текущий раунд доигрывается до конца, заглушку рисуем поверх, не обрывая раунд.
  isDisabled: false,
  roundId: null,                // строка 32 символа
  bettingMs: 0,                 // остаток окна ставок (мс)
  bettingDeadlineAt: null,      // абсолютный дедлайн приёма ставок (для локального тикера)
  startTime: null,              // время старта роста (мс, из crash:run)
  growth: 0,                    // темп роста (из crash:run)
  clockOffset: 0,               // поправка часов: serverTime - Date.now() (из crash:time)
  limits: null,                 // { min_bet, max_bet, min_auto_cashout, max_auto_cashout, items_enabled, max_items }

  // ──────────────────────────── Множитель (рассчитывается в useCrashMultiplier) ────────────────────────────
  multiplierHundreds: 100,      // последнее значение множителя в сотых (100 = 1.00×)
  crashPoint: null,             // точка краха в сотых (из crash:bust)
  lastTickAt: 0,                // время последнего crash:tick (мс) — для watchdog
  lastTickMult: 100,            // множитель последнего tick (на нём замораживаемся)
  isStale: false,               // тики пропали > TICK_TIMEOUT_MS — заморозили
  showOfflineNotice: false,     // тики пропали > OFFLINE_NOTICE_MS — показать плашку

  // ──────────────────────────── Игроки и ставки ────────────────────────────
  // Map<chatId, playerState> — игроки текущего раунда, идемпотентно обновляются.
  // playerState: { chatId, name, ava, slug, team, amount, crystal, itemsSum, items, autoCashout, cashed, cashoutMultiplier, win, winSkin }
  // team — банда игрока ({slug, name, ava} либо null), фиксируется на момент подключения к сокету
  players: new Map(),
  myChatId: null,               // chatId текущего игрока (вытаскиваем из crash:bet)
  myBet: null,                  // своя ставка (отдельным объектом из crash:bet, для UI)
  lastBust: null,               // последний crash:bust.players — для показа итогов (свой результат ищем по chatId)

  // ──────────────────────────── Баланс и инвентарь ────────────────────────────
  balance: 0,
  inventory: [],                // [{ id, name, img, color, game, cost }] — для ставки, замещается целиком

  // ──────────────────────────── Лента истории сверху (сокет) ────────────────────────────
  history: [],                  // массив точек краха в сотых, свежие первыми

  // ──────────────────────────── Состояние UI ────────────────────────────
  isPlacingBet: false,          // кнопка «Поставить» → «отправляем» (до crash:bet / crash:alert)
  isCashingOut: false,          // блокировка кнопки «Вывести» после клика (до crash:cashout)
  cashoutTimeoutId: null,       // страховка: снять блокировку, если подтверждение не пришло (§8.5)
  pendingWinSkinChatIds: new Set(),  // у кого в этом раунде ждём win-skin (для плашки)
  winSkinTimeouts: new Map(),   // Map<chatId, timeoutId> — страховка по WIN_SKIN_TIMEOUT_MS
  lastAlertCode: null,          // код последнего crash:alert (для показа ошибки в UI)

  // ──────────────────────────── Авторизация ────────────────────────────
  setConnected: (connected) => set({ isConnected: connected, hasSnapshot: false, hasRoundSnapshot: false }),
  setAuthorized: (authorized) => set({ isAuthorized: authorized }),
  setShowReconnectNotice: (show) => set({ showReconnectNotice: show }),

  // ──────────────────────────── События сервера → клиент ────────────────────────────

  /**
   * crash:time — приходят серверные часы.
   * Разницу (serverTime - Date.now()) сохраняем один раз, учитываем при расчёте множителя.
   */
  onTime: (serverTime) => {
    const offset = serverTime - Date.now()
    set({ clockOffset: offset })
  },

  /**
   * crash:round — открыт раунд, идёт приём ставок.
   * В снапшоте после реконнекта bettingMs уже урезан до остатка.
   */
  onRound: (data) => {
    const now = Date.now()
    // crash:players приходит только при подключении, а не на каждый раунд —
    // список игроков прошлого раунда чистим сами (иначе останутся «призраки» с cashed: true)
    const isNewRound = data.roundId !== get().roundId
    get().clearCashoutTimeout()
    set({
      phase: PHASES.BETTING,
      hasRoundSnapshot: true,
      isDisabled: false,            // событие «включили обратно» — это следующий crash:round (§4)
      roundId: data.roundId,
      players: isNewRound ? new Map() : get().players,
      lastBust: isNewRound ? null : get().lastBust,
      bettingMs: data.bettingMs,
      bettingDeadlineAt: now + data.bettingMs,
      limits: data.limits || get().limits,
      startTime: null,
      growth: 0,
      multiplierHundreds: 100,
      crashPoint: null,
      lastTickAt: 0,
      lastTickMult: 100,
      isStale: false,
      showOfflineNotice: false,
      // Снимаем состояния ставки/вывода прошлого раунда
      isPlacingBet: false,
      isCashingOut: false,
      myBet: isNewRound ? null : get().myBet,
      pendingWinSkinChatIds: isNewRound ? new Set() : get().pendingWinSkinChatIds
    })
    // Очищаем таймауты win-skin прошлого раунда
    if (isNewRound) get().clearWinSkinTimeouts()
  },

  /**
   * crash:run — множитель пошёл вверх. Ставки закрыты.
   */
  onRun: (data) => {
    // В снапшоте может прийти с snapshot:true и limits — берём через ?.
    const updates = {
      phase: PHASES.RUNNING,
      hasRoundSnapshot: true,
      startTime: data.startTime,
      growth: data.growth,
      multiplierHundreds: 100,
      lastTickAt: Date.now(),
      lastTickMult: 100,
      isStale: false,
      showOfflineNotice: false
    }
    if (data.limits) updates.limits = data.limits
    if (data.roundId && data.roundId !== get().roundId) {
      updates.roundId = data.roundId
      updates.myBet = null
      updates.isPlacingBet = false
      updates.isCashingOut = false
      updates.pendingWinSkinChatIds = new Set()
    }
    set(updates)
  },

  /**
   * crash:tick — коррекция расхождения, не источник кадра.
   * Обновляем lastTickAt и lastTickMult для watchdog.
   * Если isStale — снимаем состояния (связь вернулась).
   */
  onTick: (data) => {
    set({
      lastTickAt: Date.now(),
      lastTickMult: data.multiplier,
      isStale: false,
      showOfflineNotice: false
    })
  },

  /**
   * crash:bust — крах. Авторитетная точка краха.
   * Останавливаем анимацию именно здесь.
   */
  onBust: (data) => {
    const playersMap = get().players
    // Своя ставка сгорела (не выведена до краха) — тактильный акцент момента
    const myChatId = get().myChatId
    const myCashed = myChatId ? playersMap.get(String(myChatId))?.cashed : false
    if (get().myBet && !myCashed) hapticImpact('heavy')
    // Обновляем итоговые результаты игроков из data.players
    if (Array.isArray(data.players)) {
      data.players.forEach((p) => {
        if (p.chatId == null) return
        playersMap.set(String(p.chatId), {
          ...playersMap.get(String(p.chatId)),
          ...p,
          cashed: p.cashoutMultiplier != null,
          cashoutMultiplier: p.cashoutMultiplier,
          win: p.win
        })
      })
    }
    get().clearCashoutTimeout()
    set({
      phase: PHASES.BUSTED,
      hasSnapshot: true,
      hasRoundSnapshot: true,
      crashPoint: data.crashPoint,
      // Режим выключен — раунд доиграли, новых не будет: список игроков чистим сами (§4)
      players: get().isDisabled ? new Map() : new Map(playersMap),
      isStale: false,
      showOfflineNotice: false,
      isCashingOut: false,
      isPlacingBet: false,
      lastBust: data
    })
  },

  /**
   * crash:bet — своя ставка принята (только тебе).
   * Снимает «загрузку» с кнопки, рисует свою строку.
   */
  onMyBet: (data) => {
    const myChatId = String(data.chatId)
    set({
      myChatId,
      myBet: data,
      isPlacingBet: false
    })
    // Ставка принята — тактильное подтверждение (сюда, по событию, а не по клику)
    hapticNotify('success')
    // Добавляем себя в общий Map (идемпотентно)
    const playersMap = get().players
    playersMap.set(myChatId, { ...playersMap.get(myChatId), ...normalizeBet(data) })
    set({ players: new Map(playersMap) })
    // Если ставка со скинами — готовимся ждать win-skin в фазе вывода
    if (Number(data.itemsSum) > 0) {
      // (плашку «подбираем предмет» показываем после вывода, см. onMyCashout)
    }
  },

  /**
   * crash:bets — ставки остальных, пачкой. Поля те же, что в crash:bet.
   * Идемпотентно обновляем Map по chatId.
   */
  onBets: (data) => {
    if (!Array.isArray(data.bets)) return
    const playersMap = get().players
    data.bets.forEach((bet) => {
      if (bet.chatId == null) return
      const key = String(bet.chatId)
      playersMap.set(key, { ...playersMap.get(key), ...normalizeBet(bet) })
    })
    set({ players: new Map(playersMap) })
  },

  /**
   * crash:cashout — твой собственный вывод зафиксирован (только тебе, мгновенно).
   * win — полная выплата (ставка × множитель), не прибыль.
   */
  onMyCashout: (data) => {
    const myChatId = String(data.chatId)
    get().clearCashoutTimeout()
    set({ isCashingOut: false })
    // Вывод состоялся — только здесь, по подтверждению сервера (не по клику)
    hapticNotify('success')
    // Обновляем свою запись в players
    const playersMap = get().players
    const existing = playersMap.get(myChatId) || {}
    playersMap.set(myChatId, {
      ...existing,
      cashed: true,
      cashoutMultiplier: data.multiplier,
      win: data.win
    })
    set({ players: new Map(playersMap) })
    // Если у ставки были скины — показываем плашку «подбираем предмет»
    const myBet = get().myBet
    if (myBet && Number(myBet.itemsSum) > 0) {
      const pendingSet = new Set(get().pendingWinSkinChatIds)
      pendingSet.add(myChatId)
      set({ pendingWinSkinChatIds: pendingSet })
      // Страховка по таймауту — погасим плашку через WIN_SKIN_TIMEOUT_MS
      get().startWinSkinTimeout(myChatId)
    }
  },

  /**
   * crash:cashouts — чужие выводы, пачкой.
   */
  onCashouts: (data) => {
    if (!Array.isArray(data.cashouts)) return
    const playersMap = get().players
    data.cashouts.forEach((c) => {
      if (c.chatId == null) return
      const key = String(c.chatId)
      const existing = playersMap.get(key) || {}
      playersMap.set(key, {
        ...existing,
        cashed: true,
        cashoutMultiplier: c.multiplier,
        win: c.win
      })
      // Если у чужого игрока был itemsSum и мы как-то отслеживали — чужие плашки не рисуем,
      // но обновим winSkin-состояние через onWinSkin (всем приходит).
    })
    set({ players: new Map(playersMap) })
  },

  /**
   * crash:win-skin — выигрыш ставки со скинами выдан предметом.
   * Приходит всем (свой узнаём по chatId). skin: null — предмет не подобрался, выплатили кристаллами.
   */
  onWinSkin: (data) => {
    const chatId = String(data.chatId)
    const playersMap = get().players
    const existing = playersMap.get(chatId) || {}
    playersMap.set(chatId, {
      ...existing,
      winSkin: data.skin
    })
    set({ players: new Map(playersMap) })
    // Снимаем плашку «подбираем предмет» (свою — по таймауту или по событию)
    const pendingSet = new Set(get().pendingWinSkinChatIds)
    if (pendingSet.has(chatId)) {
      pendingSet.delete(chatId)
      set({ pendingWinSkinChatIds: pendingSet })
    }
    get().clearWinSkinTimeout(chatId)
  },

  /**
   * crash:inventory — твой инвентарь для ставки, полным списком (замещаем целиком).
   */
  onInventory: (data) => {
    if (!Array.isArray(data.items)) return
    set({ inventory: data.items })
  },

  /**
   * crash:players — полный список ставок текущего раунда. При подключении — восстановление.
   * Замещаем список целиком, не мержим.
   */
  onPlayers: (data) => {
    if (!Array.isArray(data.players)) return
    const playersMap = new Map()
    let myBet = null
    data.players.forEach((p) => {
      if (p.chatId == null) return
      playersMap.set(String(p.chatId), { ...p })
      // Восстанавливаем свою ставку после реконнекта (ищем себя по chatId)
      const myChatId = get().myChatId
      if (myChatId && String(p.chatId) === String(myChatId)) {
        myBet = { ...p }
      }
    })
    set({ players: playersMap, myBet, hasSnapshot: true, isPlacingBet: false, isCashingOut: false,
      pendingWinSkinChatIds: myBet ? get().pendingWinSkinChatIds : new Set() })
  },

  /**
   * crash:history — лента последних точек краха (до 20, свежие первыми).
   */
  onHistory: (data) => {
    if (!Array.isArray(data.rounds)) return
    set({ history: data.rounds })
  },

  /**
   * crash:balance — баланс игрока. Единственный источник правды.
   * Приходит адресно: после авторизации, после ставки, после выигрыша, при изменении вне краша.
   */
  onBalance: (data) => {
    // Первый crash:balance = факт авторизации (наблюдателю не приходит)
    set({
      balance: Number(data.balance),
      isAuthorized: true
    })
  },

  /**
   * crash:alert — ставка или вывод отклонены. Коды — см. §7.
   * Здесь только снимаем «загрузку» с кнопок; текст ошибки компонент покажет по коду.
   */
  onAlert: (data) => {
    get().clearCashoutTimeout()
    // Отказ сервера — виброотклик ошибки (клик уже откликнулся по нажатию)
    hapticNotify('error')
    set({
      isPlacingBet: false,
      isCashingOut: false,
      lastAlertCode: data.code
    })
  },

  /**
   * crash:disabled — режим выключен админом.
   * Текущий раунд доигрывается — фазу не трогаем (кнопка «Вывести» и множитель
   * продолжают работать), только поднимаем флаг для заглушки. Если раунда нет
   * (пришло при подключении вместо crash:round) — crash:players не придёт, чистим список сами.
   */
  onDisabled: () => {
    const { phase } = get()
    const roundInProgress = get().hasRoundSnapshot && (phase === PHASES.BETTING || phase === PHASES.RUNNING)
    set({
      isDisabled: true,
      hasSnapshot: roundInProgress ? get().hasSnapshot : true,
      players: roundInProgress ? get().players : new Map(),
      ...(!get().hasRoundSnapshot ? { phase: PHASES.IDLE, roundId: null, myBet: null,
        isPlacingBet: false, isCashingOut: false, pendingWinSkinChatIds: new Set() } : {})
    })
  },

  // ──────────────────────────── Действия ────────────────────────────

  /**
   * Стартовая инициализация при монтировании экрана:
   * запрос /api/crash/inventory/ для первой отрисовки, пока поднимается сокет.
   * Дальше баланс и инвентарь приедут по сокету.
   */
  initFromRest: async (inventoryOnly = false) => {
    try {
      const data = await crashApi.getInventory(undefined, () => inventoryOnly || get().isAuthorized)
      set({
        balance: inventoryOnly || get().isAuthorized ? get().balance : Number(data.balance || 0),
        inventory: Array.isArray(data.inventory) ? data.inventory : [],
        limits: data.limits || get().limits
      })
    } catch (error) {
      console.error('Crash: не удалось получить инвентарь через REST, ждём сокет', error)
    }
  },

  /**
   * Поставить.
   * autoCashout — в ИКСАХ (2.0), переводит сервер в сотые (200). §5.
   * items — массив slug-ов (поле id из crash:inventory).
   */
  placeBet: ({ amount, items, autoCashout }) => {
    const { roundId, isConnected } = get()
    if (!roundId || !isConnected) return false
    const sent = crashSocket.emit(CLIENT_EVENTS.PLACE_BET, {
      roundId,
      amount: Number(amount) || 0,
      items: Array.isArray(items) ? items : [],
      autoCashout: Number(autoCashout) || 0  // в иксах!
    })
    if (sent) set({ isPlacingBet: true, lastAlertCode: null })
    return sent
  },

  /**
   * Вывести. Множитель не передаётся — фиксирует сервер по своему таймеру.
   * Блокируем кнопку сразу, до crash:cashout (не «оптимистично» — §8.5).
   */
  cashout: () => {
    const { roundId, isCashingOut, isConnected } = get()
    if (!roundId || isCashingOut || !isConnected) return false
    if (!crashSocket.emit(CLIENT_EVENTS.CASHOUT, { roundId })) return false
    get().clearCashoutTimeout()
    // Страховка (§8.5): если emit ушёл в момент разрыва, crash:cashout не придёт —
    // через CASHOUT_CONFIRM_TIMEOUT_MS снимаем блокировку, чтобы игрок мог нажать ещё раз
    // (повторный клик безвреден — сервер игнорирует). Вывод считаем состоявшимся только по crash:cashout.
    const timeoutId = setTimeout(() => {
      set({ isCashingOut: false, cashoutTimeoutId: null })
    }, CASHOUT_CONFIRM_TIMEOUT_MS)
    set({ isCashingOut: true, cashoutTimeoutId: timeoutId })
    return true
  },

  /**
   * Снять блокировку кнопки «Вывести» и погасить таймаут-страховку.
   * Вызывается по подтверждению/отказу/крах/новому раунду и на disconnect.
   */
  clearCashoutTimeout: () => {
    const { cashoutTimeoutId } = get()
    if (cashoutTimeoutId) {
      clearTimeout(cashoutTimeoutId)
      set({ cashoutTimeoutId: null })
    }
  },

  unlockCashout: () => {
    get().clearCashoutTimeout()
    set({ isCashingOut: false })
  },

  /**
   * chatId текущего игрока из контекста авторизации — чтобы найти свою ставку
   * в crash:players после реконнекта/перезагрузки, ещё до первого crash:bet (§8.4).
   * crash:bet при этом остаётся авторитетным источником и перезапишет значение.
   */
  setMyChatId: (chatId) => {
    if (chatId == null || chatId === '') return
    set({ myChatId: String(chatId) })
  },

  /**
   * Обновление множителя из useCrashMultiplier (RAF-цикл).
   */
  setMultiplier: (multiplierHundreds, isStale, showOfflineNotice) => {
    set({
      multiplierHundreds,
      isStale,
      showOfflineNotice
    })
  },

  /**
   * Сброс при размонтировании экрана.
   */
  reset: (preserveGame = false) => {
    if (preserveGame) {
      set({ isConnected: false, hasSnapshot: false, hasRoundSnapshot: false, isAuthorized: false, showReconnectNotice: false })
      return
    }
    get().clearWinSkinTimeouts()
    get().clearCashoutTimeout()
    set({
      phase: PHASES.IDLE,
      isConnected: false,
      hasSnapshot: false,
      hasRoundSnapshot: false,
      isAuthorized: false,
      isDisabled: false,
      roundId: null,
      lastBust: null,
      bettingMs: 0,
      bettingDeadlineAt: null,
      startTime: null,
      growth: 0,
      multiplierHundreds: 100,
      crashPoint: null,
      isStale: false,
      showOfflineNotice: false,
      isPlacingBet: false,
      isCashingOut: false,
      myBet: null,
      players: new Map(),
      pendingWinSkinChatIds: new Set()
    })
  },

  // ──────────────────────────── Страховки win-skin ────────────────────────────

  /**
   * Запустить таймаут ожидания win-skin для игрока — погасить плашку через WIN_SKIN_TIMEOUT_MS.
   * Текст страховки: «проверьте инвентарь», а не «ошибка» (§5a).
   */
  startWinSkinTimeout: (chatId) => {
    const key = String(chatId)
    const existing = get().winSkinTimeouts.get(key)
    if (existing) clearTimeout(existing)
    const timeoutId = setTimeout(() => {
      const pendingSet = new Set(get().pendingWinSkinChatIds)
      if (pendingSet.has(key)) {
        pendingSet.delete(key)
        set({ pendingWinSkinChatIds: pendingSet })
      }
      const timeouts = new Map(get().winSkinTimeouts)
      timeouts.delete(key)
      set({ winSkinTimeouts: timeouts })
    }, WIN_SKIN_TIMEOUT_MS)
    const timeouts = new Map(get().winSkinTimeouts)
    timeouts.set(key, timeoutId)
    set({ winSkinTimeouts: timeouts })
  },

  clearWinSkinTimeout: (chatId) => {
    const key = String(chatId)
    const timeoutId = get().winSkinTimeouts.get(key)
    if (timeoutId) {
      clearTimeout(timeoutId)
      const timeouts = new Map(get().winSkinTimeouts)
      timeouts.delete(key)
      set({ winSkinTimeouts: timeouts })
    }
  },

  clearWinSkinTimeouts: () => {
    get().winSkinTimeouts.forEach((id) => clearTimeout(id))
    set({ winSkinTimeouts: new Map() })
  }
}))

export default useCrashStore
